import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { EntityIcon } from "@/components/ui/cores";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination } from "@/components/datatable/DataTable";
import { AuditoriaService } from "@/services";
import { Auditoria, OperacaoAuditoria, OrigemAuditoria, UsuarioAuditoria } from "@/types/Auditoria";
import { PagedResponse } from "@/types/PagedResponse";
import { getTenant } from "@/utils/TenantUtils";
import { ErrorPage } from "../ErrorPage";

const ORIGENS: { valor: OrigemAuditoria; nome: string }[] = [
  { valor: 'SEGURO', nome: 'Seguro' },
  { valor: 'SINISTRO', nome: 'Sinistro' },
  { valor: 'SEGURADO', nome: 'Segurado' },
  { valor: 'SEGURADORA', nome: 'Seguradora' },
  { valor: 'USUARIO', nome: 'Usuário' },
  { valor: 'CORRETORA', nome: 'Corretora' },
  { valor: 'ANEXO', nome: 'Anexo' },
];

const OPERACOES: Record<OperacaoAuditoria, { nome: string; cor: string }> = {
  INCLUSAO: { nome: 'Inclusão', cor: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' },
  ALTERACAO: { nome: 'Alteração', cor: 'bg-blue-500/10 text-blue-700 dark:text-blue-300' },
  EXCLUSAO: { nome: 'Exclusão', cor: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' },
};

const TODOS = 'todos';

// Link para o registro alterado (não existe mais quando foi excluído)
const linkRegistro = (origem: OrigemAuditoria, item: Auditoria) => {
  if (item.operacao === 'EXCLUSAO') return undefined;
  const t = getTenant();
  switch (origem) {
    case 'SEGURO': return `/${t}/seguros/${item.registro}/resumo`;
    case 'SINISTRO': return item.registroPai && `/${t}/seguros/${item.registroPai}/sinistro/${item.registro}`;
    case 'SEGURADO': return `/${t}/segurado/${item.registro}`;
    case 'SEGURADORA': return `/${t}/seguradora/${item.registro}`;
    case 'USUARIO': return `/${t}/usuario/${item.registro}`;
    case 'CORRETORA': return `/${t}/empresa`;
    default: return undefined;
  }
}

// Filtros na URL: voltar para a tela mantém a pesquisa
export const AuditoriaPage = () => {
  const [params, setParams] = useSearchParams();
  const origem = (params.get('origem') || 'SEGURO') as OrigemAuditoria;
  const inicio = params.get('inicio') || '';
  const fim = params.get('fim') || '';
  const usuario = params.get('usuario') || '';
  const page = Number(params.get('page') || 1);

  const [dados, setDados] = useState<PagedResponse<Auditoria>>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError>();
  const [usuarios, setUsuarios] = useState<UsuarioAuditoria[]>([]);

  const alterar = (campo: string, valor?: string) => {
    const novos = new URLSearchParams(params);
    valor ? novos.set(campo, valor) : novos.delete(campo);
    novos.delete('page');
    setParams(novos);
  }

  useEffect(() => {
    AuditoriaService.usuarios().then(r => setUsuarios(r.data)).catch(() => setUsuarios([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    AuditoriaService.buscar({ origem, inicio: inicio || undefined, fim: fim || undefined, usuario: usuario || undefined, page: page - 1, size: 20 })
      .then(r => setDados(r.data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [origem, inicio, fim, usuario, page]);

  if (error) return <ErrorPage axiosError={error} />;

  return (
    <div className="space-y-4">
      <PageHeader icon={<EntityIcon entidade="auditoria" />} title="Auditoria" total={dados?.totalElements} />
      <Card className="overflow-hidden shadow-none">
        <div className="grid gap-3 border-b p-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Origem</Label>
            <Select value={origem} onValueChange={v => alterar('origem', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORIGENS.map(o => <SelectItem key={o.valor} value={o.valor}>{o.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="auditoria-inicio">De</Label>
            <Input id="auditoria-inicio" type="date" value={inicio} max={fim || undefined} onChange={e => alterar('inicio', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="auditoria-fim">Até</Label>
            <Input id="auditoria-fim" type="date" value={fim} min={inicio || undefined} onChange={e => alterar('fim', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Usuário</Label>
            <Select value={usuario || TODOS} onValueChange={v => alterar('usuario', v === TODOS ? undefined : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos</SelectItem>
                {usuarios.map(u => <SelectItem key={u.id} value={u.id}>{u.nome || 'Sem nome'}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && !dados ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-5 w-1/2" /><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-2/3" />
          </div>
        ) : !dados?.content.length ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Nenhuma alteração encontrada.</p>
        ) : (
          <ul className={`divide-y ${loading ? 'opacity-60' : ''}`}>
            {dados.content.map(item => <ItemAuditoria key={`${item.revisao}-${item.registro}`} origem={origem} item={item} />)}
          </ul>
        )}
        <Pagination pagination={dados} />
      </Card>
    </div>
  );
}

const ItemAuditoria = ({ origem, item }: { origem: OrigemAuditoria; item: Auditoria }) => {
  const operacao = OPERACOES[item.operacao];
  const link = linkRegistro(origem, item);
  const descricao = item.descricao || 'Registro';
  const quando = new Date(item.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <li className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Badge className={operacao.cor}>{operacao.nome}</Badge>
        {link
          ? <Link to={link} className="min-w-0 break-words font-medium text-primary hover:underline">{descricao}</Link>
          : <span className="min-w-0 break-words font-medium">{descricao}</span>}
        <span className="ml-auto text-sm text-muted-foreground">
          {quando} · {item.usuarioNome || 'Sistema'}
        </span>
      </div>
      {item.operacao === 'EXCLUSAO' ? (
        <p className="text-sm text-muted-foreground">Registro excluído.</p>
      ) : item.alteracoes.length > 0 && (
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(8rem,auto)_1fr]">
          {item.alteracoes.map((a, i) => (
            <div key={i} className="contents">
              <dt className="text-muted-foreground max-sm:mt-1">{a.campo}</dt>
              <dd className="flex min-w-0 flex-wrap items-center gap-x-2 break-words">
                {a.antes != null && <span className="text-muted-foreground line-through decoration-muted-foreground/50">{a.antes}</span>}
                {a.antes != null && a.depois != null && <ArrowRight aria-label="para" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                {a.depois != null ? <span>{a.depois}</span> : <span className="text-muted-foreground">(removido)</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}
